import { isEmpty } from 'lodash';
import config from 'react-global-configuration';
import i18next from 'i18next';

import { setPaidUser, setGoogleIntegrationExists, updateLocalStorage } from '@utils/utils';
import { getCachedData, setCachedData, clearCachedData, CACHE_KEYS, CACHE_CONFIG } from '@utils/settingsCache';
import Mixpanel from '@analytics/mixpanel';
import apiClient from '@api/apiClient';
import apiProfile from '@api/apiProfile';
import apiPayment from '@api/apiPayment';
import notificationService from '@utils/notificationService';

export const START_REQUEST = 'START_REQUEST';
export const STOP_REQUEST = 'STOP_REQUEST';
export const FETCH_PROFILE_START = 'FETCH_PROFILE_START';
export const FETCH_PROFILE_SUCCESS = 'FETCH_PROFILE_SUCCESS';
export const FETCH_PROFILE_FAILURE = 'FETCH_PROFILE_FAILURE';
export const FETCH_BILLINGINFO_START = 'FETCH_BILLINGINFO_START';
export const FETCH_BILLINGINFO_SUCCESS = 'FETCH_BILLINGINFO_SUCCESS';
export const FETCH_INTEGRATIONS_START = 'FETCH_INTEGRATIONS_START';
export const FETCH_INTEGRATIONS_SUCCESS = 'FETCH_INTEGRATIONS_SUCCESS';
export const FETCH_INTEGRATIONS_FAILURE = 'FETCH_INTEGRATIONS_FAILURE';
export const INVALIDATE_GOOGLE_INTEGRATION = 'INVALIDATE_GOOGLE_INTEGRATION';
export const FETCH_USER_SETTINGS_SUCCESS = 'FETCH_USER_SETTINGS_SUCCESS';
export const UPDATE_USER_SETTINGS_SUCCESS = 'UPDATE_USER_SETTINGS_SUCCESS';

const STRIPE_API_KEYS = config.get('stripeApiKeys');
const DEFAULT_REGION = 'main';

const apiDomainWithProtocol = `${window.location.protocol}//${config.get('apiDomain')}`;

export const startRequest = () => ({ type: START_REQUEST });
export const stopRequest = () => ({ type: STOP_REQUEST });

export const fetchProfile = () => async (dispatch) => {
  dispatch({ type: FETCH_PROFILE_START });
  try {
    const { data } = await apiClient.get('/profile');
    const { account_status, integrations = {} } = data;
    const { google } = integrations;
    updateLocalStorage('emailVerified', data.email_verified);

    setPaidUser(account_status === 'valid');
    setGoogleIntegrationExists(!!google);

    dispatch({ profile: data, type: FETCH_PROFILE_SUCCESS });
    return data;
  } catch (error) {
    dispatch({ type: FETCH_PROFILE_FAILURE });
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
  return {};
};

export const updateNotifications = (notifications) => async (dispatch, getState) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.updateNotifications(notifications);

    const { profile } = getState().profileReducer;
    dispatch({
      profile: { ...profile, notifications: { ...profile.notifications, ...notifications } },
      type: FETCH_PROFILE_SUCCESS
    });

    notificationService.success({ message: data });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const fetchBillingInfo = () => async (dispatch) => {
  dispatch({ type: FETCH_BILLINGINFO_START });
  dispatch(startRequest());
  try {
    const { data } = await apiClient.get('/profile/billing_info');
    dispatch({ billingInfo: data || {}, type: FETCH_BILLINGINFO_SUCCESS });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const updateBillingInfo = (payload) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiClient.put('/profile/billing_info', payload);
    notificationService.success({ message: data });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Update billing info');
};

export const removeProfile = (navigate, redirect = '/logout') => async (dispatch) => {
  Mixpanel.track('Remove profile');

  dispatch(startRequest());
  try {
    const { data } = await apiClient.delete('/profile');

    notificationService.success({ message: data, duration: 6 });

    setTimeout(() => navigate(redirect), 5000);
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const inviteTenant = (email) => async (dispatch) => {
  Mixpanel.track('Invite tenant');

  dispatch(startRequest());
  try {
    const { data } = await apiProfile.inviteTenant(email);
    await dispatch(fetchProfile());
    if (typeof data === 'string') notificationService.success({ message: data });
    return data;
  } catch (error) {
    notificationService.error({ message: error.message });
    throw error;
  } finally {
    dispatch(stopRequest());
  }
};

export const resendTenant = (tenantUserId, captcha) => async (dispatch) => {
  Mixpanel.track('Resend tenant invitation');

  dispatch(startRequest());
  try {
    const { data } = await apiProfile.resendTenant(tenantUserId, captcha);
    await dispatch(fetchProfile());
    if (typeof data === 'string') notificationService.success({ message: data });
    return data;
  } catch (error) {
    notificationService.error({ message: error.message });
    throw error;
  } finally {
    dispatch(stopRequest());
  }
};

export const removeTenant = (tenantUserId) => async (dispatch) => {
  Mixpanel.track('Remove tenant');

  dispatch(startRequest());
  try {
    const { data } = await apiProfile.removeTenant(tenantUserId);
    await dispatch(fetchProfile());
    notificationService.success({ message: data });
    return data;
  } catch (error) {
    notificationService.error({ message: error.message });
    throw error;
  } finally {
    dispatch(stopRequest());
  }
};

export const stopSubscription = (subscription_id, cancel_at_period_end = false) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiClient.post('/stop-customer-subscription', { subscription_id, cancel_at_period_end });

    notificationService.success({ message: data, description: i18next.t('description.invoiceFinalizeEmail'), duration: 10 });
    dispatch(fetchProfile());
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Stop subscription');
};

export const voidSubscription = (subscription_id) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiClient.post('/void-customer-subscription', { subscription_id });

    notificationService.success({ message: data });
    dispatch(fetchProfile());
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Clear subscription');
};

export const connectPaymentMethod = (secondaryTab = false, region = null) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiClient.post('/create-checkout-session', { secondaryTab });
    const { sessionId } = data;

    let stripe = window.Stripe(STRIPE_API_KEYS[region || DEFAULT_REGION]);
    stripe.redirectToCheckout({ sessionId }).then((result) => {
      notificationService.error({ message: result.error.message });
    });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const makePayment = (deal, region = null, secondaryTab = false) => async (dispatch) => {
  try {
    dispatch(startRequest());

    const { data } = await apiClient.post('/create-payment-checkout-session', { deal, secondaryTab });
    const { sessionId } = data;
    let stripe = window.Stripe(STRIPE_API_KEYS[region || DEFAULT_REGION]);

    stripe.redirectToCheckout({ sessionId }).then((result) => {
      notificationService.error({ message: result.error.message });
    });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export async function nmiCharge(token, amount, onSuccess = null, onError = null) {
  try {
    const { data } = await apiPayment.nmiCharge(token, amount);
    const { response, responsetext } = data;

    if (response === '1') {
      notificationService.success({ message: i18next.t('message.paymentSuccessful'), duration: 0 });
      if (onSuccess) onSuccess();
    } else {
      notificationService.error({ message: i18next.t('message.paymentUnsuccessfulWithReason', { reason: responsetext }), duration: 0 });
      if (onError) onError();
    }
  } catch (error) {
    notificationService.error({ message: error.message });
    if (onError) onError();
  }
}

export const removePaymentMethod = () => async (dispatch) => {
  try {
    dispatch(startRequest());

    await apiClient.delete('/profile/payment_method');
    notificationService.success({ message: i18next.t('message.removed') });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Remove payment method');
};

export const getAPIToken = () => async (dispatch, getState) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.getAPIToken();

    if (data.is_created) {
      notificationService.success({ message: i18next.t('message.apiTokenCreatedSuccessfully') });
    }

    const { profile } = getState().profileReducer;
    dispatch({
      profile: { ...profile, api_token: data.api_token },
      type: FETCH_PROFILE_SUCCESS
    });
    return data.api_token;
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const createAPIToken = () => async (dispatch, getState) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.createAPIToken();
    notificationService.success({ message: i18next.t('message.apiTokenCreatedSuccessfully') });

    const { profile } = getState().profileReducer;
    dispatch({
      profile: { ...profile, api_token: data.api_token },
      type: FETCH_PROFILE_SUCCESS
    });
    return data.api_token;
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Create API token');
};

export const revokeAPIToken = () => async (dispatch, getState) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.revokeAPIToken();
    notificationService.success({ message: data });

    const { profile } = getState().profileReducer;
    dispatch({
      profile: { ...profile, api_token: null },
      type: FETCH_PROFILE_SUCCESS
    });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Revoke API token');
};

export const invalidateGoogleIntegration = () => (dispatch) => {
  setGoogleIntegrationExists(false);
  clearCachedData(CACHE_KEYS.INTEGRATIONS);
  dispatch({ type: INVALIDATE_GOOGLE_INTEGRATION });
};

export const fetchIntegrations = (forceReload = false) => async (dispatch, getState) => {
  if (!forceReload && !isEmpty(getState().profileReducer.integrations)) {
    return;
  }

  if (!forceReload) {
    const cachedIntegrations = getCachedData(CACHE_KEYS.INTEGRATIONS, CACHE_CONFIG.INTEGRATIONS_EXPIRATION);
    if (cachedIntegrations) {
      dispatch({ integrations: cachedIntegrations, type: FETCH_INTEGRATIONS_SUCCESS });
      return;
    }
  }

  dispatch({ type: FETCH_INTEGRATIONS_START });
  try {
    const { data } = await apiProfile.fetchIntegrations();

    setCachedData(CACHE_KEYS.INTEGRATIONS, data);
    dispatch({ integrations: data, type: FETCH_INTEGRATIONS_SUCCESS });
  } catch (error) {
    dispatch({ type: FETCH_INTEGRATIONS_FAILURE });
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const updateIntegrations = (integrations, callback = null) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.updateIntegrations(integrations);

    clearCachedData(CACHE_KEYS.INTEGRATIONS);
    notificationService.success({ message: data });

    if (callback) callback();
    return true;
  } catch (error) {
    notificationService.error({ message: error.message });
    return false;
  } finally {
    dispatch(stopRequest());
  }
};

export const removeIntegration = (integration, showNotification = true) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.removeIntegration(integration);

    if (integration === 'google') setGoogleIntegrationExists(false);
    clearCachedData(CACHE_KEYS.INTEGRATIONS);

    if (showNotification) notificationService.success({ message: data });
    dispatch(fetchIntegrations(true));
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const connectIntegration = (code, app, region, callback = null, exportAfterAuth = false, connectUser = null) => async (dispatch) => {
  let credentials = null;

  if (app === 'google') {
    credentials = await connectGoogleSheetsAndReturnCredentials();
    if (!credentials) return null;
  }

  dispatch(startRequest());

  try {
    let data = null;

    if (app === 'hubspot' || !app) {
      ({ data } = await apiProfile.connectHubSpot(code));
    } else if (app === 'zoho') {
      ({ data } = await apiProfile.connectZoho(code, region));
    } else if (app === 'pipedrive') {
      ({ data } = await apiProfile.connectPipedrive(code));
    } else if (app === 'google' && credentials) {
      ({ data } = await apiProfile.connectGoogle(credentials));
      setGoogleIntegrationExists(true);
    } else if (app === 'sftp' && connectUser) {
      ({ data } = await apiProfile.connectSFTP(connectUser));
    } else {
      data = null;
    }

    if (exportAfterAuth) {
      notificationService.info({
        message: i18next.t('message.exportingDataToIntegration'),
        duration: 12
      });
    } else {
      const message = typeof data === 'string' ? data : data?.message;

      if (message) {
        notificationService.success({
          message,
          duration: 8
        });
      }
    }

    clearCachedData(CACHE_KEYS.INTEGRATIONS);
    await dispatch(fetchIntegrations(true));

    if (callback) callback();
  } catch (error) {
    notificationService.error({ message: error.message });
    if (app === 'google') {
      credentials = null;
    }
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Connect integration', { app });
  return app === 'google' ? credentials : null;
};

export async function connectGoogleSheetsAndReturnCredentials(callback = null) {
  return await new Promise((resolve) => {
    const oauthUrl = new URL('/auth/oauth2-authorize', `${apiDomainWithProtocol}/`);
    const expectedOrigin = oauthUrl.origin;
    const authWindow = window.open(oauthUrl.toString(), '_blank', 'width=500,height=600');
    if (!authWindow) {
      notificationService.warning({
        message: i18next.t('message.googleSheetsPopupBlocked'),
        description: i18next.t('description.googleSheetsPopupBlocked'),
      });
      resolve(null);
      return;
    }

    const messageListener = (event) => {
      if (event.origin !== expectedOrigin || event.source !== authWindow) return;
      if (!event.data || typeof event.data !== 'object' || Array.isArray(event.data)) return;

      const { access_token, refresh_token } = event.data;
      const hasValidTokens = typeof access_token === 'string' && access_token.trim().length > 0
        && typeof refresh_token === 'string' && refresh_token.trim().length > 0
        && refresh_token !== 'None';

      if (!hasValidTokens) return;

      clearInterval(checkClosed);
      window.removeEventListener('message', messageListener);
      authWindow.close();

      const credentials = { access_token, refresh_token };
      if (callback) callback(credentials);
      resolve(credentials);
    };

    window.addEventListener('message', messageListener);

    const checkClosed = setInterval(() => {
      if (authWindow.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', messageListener);
        resolve(null);
      }
    }, 500);
  });
}

export const updateGodMode = (email, callback = null) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.updateGodMode(email);
    notificationService.success({ message: data });

    if (callback) {
      await callback();
      return;
    }
  } catch (error) {
    notificationService.error({ message: error.message });
  }
  dispatch(stopRequest());
};

export const removeAndBlockUserByIP = (email) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.removeAndBlockUserByIP(email);
    notificationService.success({ message: data });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const unsuspendUser = (email) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.unsuspendUser(email);
    notificationService.success({ message: data });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const fetchUserSettings = (forceReload = false) => async (dispatch, getState) => {
  const { userSettings } = getState().profileReducer;
  if (!forceReload && !isEmpty(userSettings)) {
    return userSettings;
  }

  const cachedSettings = getCachedData(CACHE_KEYS.USER_SETTINGS, CACHE_CONFIG.SETTINGS_EXPIRATION);
  if (!forceReload && cachedSettings) {
    dispatch({ userSettings: cachedSettings, type: FETCH_USER_SETTINGS_SUCCESS });
    return cachedSettings;
  }

  dispatch(startRequest());
  try {
    const { data } = await apiProfile.getUserSettings();
    setCachedData(CACHE_KEYS.USER_SETTINGS, data);
    dispatch({ userSettings: data, type: FETCH_USER_SETTINGS_SUCCESS });
    return data;
  } catch (error) {
    if (error.message === 'Request failed with status code 401') {
      console.log('401 received, skipping error handling');
    } else {
      notificationService.error({ message: error.message });
    }
  } finally {
    dispatch(stopRequest());
  }
  return {};
};

export const updateUserSettings = (settings, callback = null) => async (dispatch, getState) => {
  dispatch(startRequest());
  try {
    const { data } = await apiProfile.updateUserSettings(settings);

    const currentSettings = getState().profileReducer.userSettings || {};
    const updatedSettings = { ...currentSettings, ...settings };

    setCachedData(CACHE_KEYS.USER_SETTINGS, updatedSettings);
    dispatch({ userSettings: updatedSettings, type: UPDATE_USER_SETTINGS_SUCCESS });
    if (callback) callback();
    return data;
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
  return {};
};
